import { useClipboard } from "@/hooks/useClipboard";
import { Incident, MapIncident } from "@/types/incidents";
import { formatRelativeTime } from "@/utils/formatTime";
import { getIncidentLocation, getMGRSCoordinates } from "@/utils/locationUtils";
import { Check, Copy } from "lucide-react";

interface LocationInfoSectionProps {
  alert: Incident | MapIncident;
}

const DisplayNames: React.FC<{ users: any[] }> = ({ users }) => {
  return (
    <div className="flex space-x-1">
      <span className="text-sm text-black font-semibold dark:text-gray-200">
        Assigned Responder(s):
      </span>
      <span className="text-sm dark:text-gray-200">
        {users.map((user) => (
          <div className="text-sm dark:text-gray-200" key={user.dispatcherID || user.name}>
            {user.name} ({user.dispatcherType})
          </div>
        ))}
      </span>
    </div>
  );
};

const LocationInfoSection = ({ alert }: LocationInfoSectionProps) => {
  const {
    CopyToClipboard: AddressCopyToClipboard,
    isCopied: isAddressCopied,
    handleCopy: handleAddressCopy,
  } = useClipboard();
  const {
    CopyToClipboard: CoordsCopyToClipboard,
    isCopied: isCoordsCopied,
    handleCopy: handleCoordsCopy,
  } = useClipboard();

  const coordinates: any = getIncidentLocation(alert);
  const formattedCoords = coordinates ? getMGRSCoordinates(coordinates) : "No coordinates available";

  return (
    <div className="px-4 py-2 border-b border-[#E3E3E3]">
      <div className="space-y-2">
        <div className="group">
          <div className="flex justify-between items-center space-x-1">
            <div className="text-sm flex items-center dark:text-gray-200">
              <span className="font-semibold min-w-[90px]">Coordinates:</span>
              <span>{coordinates ? `${coordinates.lat}, ${coordinates.lng}` : "No location data"}</span>
            </div>

          </div>
          {alert?.locationAddress && (
            <div className="flex justify-between items-center space-x-1">
              <div className="flex space-x-1 items-start">
                <div className="text-sm text-black font-semibold dark:text-gray-200 min-w-[90px]">
                  Address:
                </div>
                <div className="text-sm flex-1 dark:text-gray-200">
                  <span>{alert?.locationAddress}</span>
                </div>
              </div>
              <div className="flex items-center">
                <AddressCopyToClipboard
                  text={`${alert?.locationAddress}`}
                  onCopy={() => {
                    handleAddressCopy("Address copied to clipboard");
                  }}
                >
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="ml-2 p-1 hover:bg-gray-100 dark:hover:bg-dark rounded-full"
                    title={
                      "Copy Address"
                    }
                  >
                    {isAddressCopied ? (
                      <Check size={14} className="text-green-600" />
                    ) : (
                      <Copy size={14} className="text-gray-600 dark:text-gray-200" />
                    )}
                  </button>
                </AddressCopyToClipboard>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between space-x-1 mt-1">
            <div className="flex items-center space-x-1">
              <span className="text-sm text-black font-semibold dark:text-gray-200 min-w-[90px]">MGRS:</span>
              <div className="text-sm font-mono flex items-center dark:text-gray-200">
                {formattedCoords}
              </div>
            </div>
            <div className="flex items-center">
              <CoordsCopyToClipboard
                text={`${formattedCoords}`}
                onCopy={() => {
                  handleCoordsCopy("MGRS copied to clipboard");
                }}
              >
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="ml-2 p-1 hover:bg-gray-100 dark:hover:bg-dark rounded-full"
                  title="Copy MGRS"
                >
                  {isCoordsCopied ? (
                    <Check size={14} className="text-green-600" />
                  ) : (
                    <Copy size={14} className="text-gray-600 dark:text-gray-200" />
                  )}
                </button>
              </CoordsCopyToClipboard>
            </div>
          </div>
        </div>
        {
          alert?.AssignedDispatchers &&
          alert?.AssignedDispatchers.length > 0 && (
            <DisplayNames users={alert.AssignedDispatchers} />
          )}
        <div className="flex space-x-1">
          <span className="text-sm text-black font-semibold dark:text-gray-200 min-w-[90px]">
            Phone Number:
          </span>
          <span className="text-sm dark:text-gray-200">
            {alert?.ReportedByUser.phoneNo || "Not available"}
          </span>
        </div>
        {(coordinates?.altitude !== undefined ||
          coordinates?.speed !== undefined) && (
            <div className="text-overline flex items-center gap-1 dark:text-gray-200">
              <div className="min-w-[90px] flex gap-1">
                {coordinates?.altitude !== undefined && (
                  <span className="!font-bold">Altitude: </span>
                )}
              </div>
              {coordinates?.altitude !== undefined && (
                <span>{coordinates.altitude.toFixed(2)}m</span>
              )}
              {coordinates?.speed !== undefined && (
                <>
                  <span className="ml-1 !font-bold">Speed: </span>
                  <span>{coordinates.speed.toFixed(2)}mph</span>
                </>
              )}
            </div>
          )}
        <div className="flex space-x-1">
          <span className="text-xs text-gray-600 italic dark:text-gray-200">
            Location update {formatRelativeTime(alert?.lastLocationTime)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default LocationInfoSection;
